import React from 'react';
import styled from 'styled-components';
import { find, size, includes, compact, map, flatMap } from 'lodash';
import { Tile, TileStatusEnum } from './Tile';
import { tileSize, boardHeight, boardWidth } from '../helpers/constants';
import { BoardState, TileDropItem, TileGroupDropItem, Orientation } from '../types/';

import { dndTypes } from '../helpers/constants';
import { Position } from './BoardWithDragLayer';
import { SelectedBoardTiles } from '../containers/SelectedBoardTiles';

export type BoardProps = {
    coordinate?: Position | null,
    item?: TileDropItem | TileGroupDropItem,
    boardState: BoardState,
    className?: string,
    onDrop: (options: {
        item: TileDropItem,
        row: number,
        column: number,
    }) => void,
    onClickTile?: (e: React.MouseEvent<HTMLDivElement>, id: number) => unknown,
    onDoubleClickTile?: (e: React.MouseEvent<HTMLDivElement>, id: number) => unknown,
    style?: any,
};

const totalWidth = tileSize * boardWidth;
const totalHeight = tileSize * boardHeight;

const BoardContainer = styled.div`
    background-color: ${(props) => props.theme.colors.yellow};
    border: 1px solid black;
    height: ${totalHeight}px;
    width: ${totalWidth}px;
    position: relative;
`;

const CellContainer = styled.div.attrs<{ left: number, top: number, children: any }>(
    ({ left, top }) => ({
        style: {
            left: left || 0,
            top: top || 0,
        },
    }),
)`
    position: absolute;
`;

const getCellCoordinates = (position: Position) => {
    return {
        top: -position.row * tileSize,
        left: position.column * tileSize,
    };
};

// Board should be zoomable and pannable, only when selecting a non-tile
export const Board = React.memo(
    React.forwardRef<HTMLDivElement, BoardProps>(
        (
            { onDoubleClickTile, onClickTile, boardState, className, coordinate, item, style },
            drop,
        ) => {
            const { selectedBoardTiles, setSelectedBoardTiles } = SelectedBoardTiles.useContainer();
            const cellsForBoardState = React.useMemo(() => {
                return map(boardState, ({ row, column, id }) => {
                    const cellCoordinates = getCellCoordinates({ row, column });
                    return (
                        <CellContainer key={`${row}.${column}`} {...cellCoordinates}>
                            <Tile
                                onBeginDrag={() => {
                                    if (!selectedBoardTiles.includes(id)) {
                                        setSelectedBoardTiles([]);
                                    }
                                }}
                                dragType={dndTypes.TILE_FROM_BOARD}
                                onDoubleClick={(e) => {
                                    if (onDoubleClickTile) {
                                        onDoubleClickTile(e, id);
                                    }
                                }}
                                onClick={(e) => {
                                    if (onClickTile) {
                                        onClickTile(e, id);
                                    }
                                }}
                                key={id}
                                id={id}
                                status={
                                    includes(selectedBoardTiles, id)
                                        ? TileStatusEnum.ACTIVE
                                        : undefined
                                }
                            />
                        </CellContainer>
                    );
                });
            }, [
                setSelectedBoardTiles,
                boardState,
                selectedBoardTiles,
                onClickTile,
                onDoubleClickTile,
            ]);
            return (
                <BoardContainer ref={drop} className={className} style={style}>
                    <>
                        {cellsForBoardState}
                        {item &&
                            flatMap(compact([coordinate]), ({ row, column }) => {
                                if (
                                    item.type === dndTypes.TILE_FROM_HAND ||
                                    item.type === dndTypes.TILE_FROM_BOARD
                                ) {
                                    if (size(selectedBoardTiles)) {
                                        const initialTile = find(boardState, { id: item.id });
                                        if (initialTile) {
                                            const deltaRow = row - initialTile.row;
                                            const deltaColumn = column - initialTile.column;
                                            return map(selectedBoardTiles, (id) => {
                                                const initialTile = find(boardState, { id });
                                                if (initialTile) {
                                                    const newRow = initialTile.row + deltaRow;
                                                    const newColumn =
                                                        initialTile.column + deltaColumn;
                                                    const cellCoordinates = getCellCoordinates({
                                                        row: newRow,
                                                        column: newColumn,
                                                    });
                                                    return (
                                                        <CellContainer
                                                            key={`${newRow}.${newColumn}`}
                                                            {...cellCoordinates}
                                                        >
                                                            <Tile
                                                                key={id}
                                                                id={id}
                                                                status={TileStatusEnum.ACTIVE}
                                                            />
                                                        </CellContainer>
                                                    );
                                                }
                                            });
                                        }
                                    } else {
                                        const cellCoordinates = getCellCoordinates({ row, column });
                                        return (
                                            <CellContainer
                                                key={`${row}.${column}`}
                                                {...cellCoordinates}
                                            >
                                                <Tile
                                                    dragType={dndTypes.TILE_FROM_BOARD}
                                                    key={item.id}
                                                    id={item.id}
                                                    status={TileStatusEnum.ACTIVE}
                                                />
                                            </CellContainer>
                                        );
                                    }
                                } else if (item.type === dndTypes.TILE_GROUP) {
                                    const { orientation, tiles } = item;
                                    return map(tiles, (id, idx) => {
                                        const computedRow =
                                            orientation === Orientation.VERTICAL ? row - idx : row;
                                        const computedColumn =
                                            orientation === Orientation.HORIZONTAL
                                                ? column + idx
                                                : column;
                                        const computedCellCoordinates = getCellCoordinates({
                                            row: computedRow,
                                            column: computedColumn,
                                        });
                                        return (
                                            <CellContainer
                                                key={`${computedRow}.${computedColumn}`}
                                                {...computedCellCoordinates}
                                            >
                                                <Tile
                                                    dragType={dndTypes.TILE_FROM_BOARD}
                                                    key={id}
                                                    id={id}
                                                    status={TileStatusEnum.ACTIVE}
                                                />
                                            </CellContainer>
                                        );
                                    });
                                }
                            })}
                    </>
                </BoardContainer>
            );
        },
    ),
);
